<template lang="pug">
  .slide-container(v-element-visibility="setVisible" ref="containerRef")
    .kbd-controls
      button.a11y.ctrl(@click="forceNav(idx)" v-for="(_, idx) in slides" :key="idx") View Slide {{idx + 1}}
    .slides(ref="scrollerRef"
      @mouseenter="interaction = true" @touchstart="touchStart"
      @mouseleave="interaction = false" @touchend="interaction = false"
      @touchcancel="interaction = false"
    )
      .height-calc(:style="heightCalcStyle")
      .slide(ref="slideARef")
        RenderBlock(:block="slides[slideA]" v-if="slideA !== null")
      .slide(ref="slideBRef")
        RenderBlock(:block="slides[slideB]" v-if="slideB !== null")
    // The ArrowNav usually has a v-model, but in this case we are going to fake it to always show both arrows
    // by hardcoding value to 1 and max to 3.
    ArrowNav.carousel-controls( :class="{'fade-out': sliding}" v-if="!force" :modelValue="1" @update:modelValue="nav($event - 1)" :max="3" prevLabel="Previous slide" nextLabel="Next slide" )
</template>
<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
import { vElementVisibility } from '@vueuse/components'
import { useSwipe } from '@vueuse/core'
import { useRogue } from '../../js/rogue'
import ArrowNav from './ArrowNav.vue'

interface SlideBlock {
  props?: {
    background?: {
      height?: number
      width?: number
    }
    tracking?: {
      name?: string
    }
  }
}

const props = withDefaults(defineProps<{
  slides?: SlideBlock[]
  autoplayDuration?: number
}>(), {
  slides: () => [],
})

const rogue = useRogue()

type ActiveSlide = 'slideA' | 'slideB'

const active = ref<ActiveSlide>('slideA')
const sliding = ref(false)
const force = ref(false)
const visible = ref(false)
const interaction = ref(false)
const slideA = ref<number | null>(0)
const slideB = ref<number | null>(null)
const positionIndex = ref(0)
const viewedSlides = ref<number[]>([])

const containerRef = ref<HTMLElement | null>(null)
const scrollerRef = ref<HTMLElement | null>(null)
const slideARef = ref<HTMLElement | null>(null)
const slideBRef = ref<HTMLElement | null>(null)

let autoplayInterval: ReturnType<typeof setInterval> | undefined

const heightCalcStyle = computed(() => {
  const { height, width } = (((props.slides[0] || {}).props || {}).background as { width: number, height: number } || { height: 9, width: 16 })

  return { paddingTop: `${(1 / (width / height)) * 100}%` }
})

function trackView(index: number) {
  if (!containerRef.value?.clientHeight) {
    return
  }
  // Track that this slide was viewed
  if (rogue && rogue.track && viewedSlides.value.indexOf(index) === -1) {
    viewedSlides.value.push(index)
    const name = ((props.slides[index]?.props || {}).tracking || {}).name || `Slide ${index}`
    rogue.track.track({ type: 'custom', event: 'View Carousel Slide', data: { slideIndex: index, slideName: name } })
  }
}

function forceNav(idx: number) {
  if (active.value === 'slideA') {
    slideA.value = idx
  } else {
    slideB.value = idx
  }
  force.value = true
}

function createInterval() {
  // Set up the autoplay
  if (autoplayInterval) {
    clearInterval(autoplayInterval)
  }

  if (props.autoplayDuration) {
    // Add a safety in case autoplayDuration is provided in seconds
    const intervalTime = props.autoplayDuration < 50 ? props.autoplayDuration * 1000 : props.autoplayDuration

    autoplayInterval = setInterval(() => {
      // We dont want to autoplay if we are actively hovering, or if its not visible on the page.
      const accessibility = document.body.classList.contains('kbd-a11y') || force.value
      if (!accessibility && visible.value && !interaction.value && document.visibilityState !== 'hidden' && positionIndex.value < 30) {
        nav(1)
      }
    }, intervalTime)
  }
}

function setVisible(val: boolean) {
  // We dont want to autoplay when the slides are not visible
  visible.value = val
  if (!val) {
    // In case the touchEnd event doesnt register, we can clear out the Interaction flag once its invisible
    interaction.value = false
  }
}

function nav(dir: number) {
  // We can't scroll multiple slides with this method
  if (sliding.value) {
    return
  }

  // Reset the autoplay interval
  createInterval()

  sliding.value = true

  const el = scrollerRef.value
  if (!el) return

  // We have two slides - slideA and slideB.
  // We are going to load the target slide into the unused div, position it to the left or right of our current,
  // then scroll to it.
  const prevSlide = active.value
  const nextSlide: ActiveSlide = prevSlide === 'slideA' ? 'slideB' : 'slideA'

  // Calculate which of the actual slide contents from Roguebuilder is going to be shown next
  const currentIndex = prevSlide === 'slideA' ? slideA.value : slideB.value
  if (currentIndex === null) return

  let newIndex = (currentIndex + dir) % props.slides.length
  // If we are wrapping around, go to the end
  if (newIndex === -1) {
    newIndex = props.slides.length - 1
  }

  // Set the slide holder to the next slide we want to show. This will cause Vue to render the new slide to the DOM
  if (nextSlide === 'slideA') {
    slideA.value = newIndex
  } else {
    slideB.value = newIndex
  }
  positionIndex.value += dir
  const newPositionPx = positionIndex.value * 100
  const nextSlideRef = nextSlide === 'slideA' ? slideARef.value : slideBRef.value
  if (nextSlideRef) {
    nextSlideRef.style.transform = `translate3d(${newPositionPx}%, 0, 0)`
  }

  setTimeout(() => {
    nextTick(() => {
      if (el) {
        el.style.transform = `translateX(${-1 * newPositionPx}%)`
      }
    })
  }, 50)

  setTimeout(() => {
    sliding.value = false
    trackView(newIndex)
    active.value = prevSlide === 'slideA' ? 'slideB' : 'slideA'
  }, 700)
}

const SWIPE_THRESHOLD_PX = 45

// useSwipe keeps its start coordinates when a gesture begins with more than one finger, so a pinch
// that drops back to one dragging finger would measure from stale coordinates and look like a swipe.
// Only a gesture that starts as a single finger is allowed to navigate.
let gestureValid = false

function touchStart(e: TouchEvent) {
  interaction.value = true
  gestureValid = e.touches.length === 1
}

useSwipe(scrollerRef, {
  threshold  : SWIPE_THRESHOLD_PX,
  onSwipeEnd : (_e, direction) => {
    if (!gestureValid) {
      return
    }

    if (direction === 'left') {
      nav(1)
    } else if (direction === 'right') {
      nav(-1)
    }
  },
})

onMounted(() => {
  createInterval()
  trackView(0)
})

onBeforeUnmount(() => {
  if (autoplayInterval) {
    clearInterval(autoplayInterval)
  }
})
</script>
<style lang="scss">
body.kbd-a11y .slide-container .kbd-controls {
  display: block;
}
</style>
<style lang="scss" scoped>

.slide-container {
  position: relative;
  overflow: hidden;
}

.slides {
  display: flex;
  transform: translateX(0);
  transition: transform 0.6s ease-in-out;
  touch-action: pan-y pinch-zoom;
}

.carousel-controls {
  opacity: 1;
  transition: opacity 0.1s linear;
  z-index: 1;
  &.fade-out {
    opacity: 0;
  }
}

.height-calc {
  width: 100%;
  opacity: 0;
}

.kbd-controls {
  display: none;

  .ctrl {
    margin: $space-s;
    cursor: pointer;
  }
}

.slide {
  width: 100%;
  transform: translate3d(0, 0, 0);
  -webkit-backface-visibility: hidden;
  position: absolute;
  margin-bottom: 0 !important;
}

</style>
